import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { DEFAULT_CERT_TYPES } from '@cls/shared';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface RoleReq { role: string; mandatory: string[]; beneficial: string[]; other: string[]; displayOrder?: number; }

interface Props {
  onClose: () => void;
  initialEditRole?: string;
}

type Cat = 'mandatory' | 'beneficial' | 'other';

function safeId(role: string): string {
  return role.replace(/\s/g, '_').replace(/[&]/g, '_');
}

export function RoleManagerModal({ onClose, initialEditRole }: Props) {
  const qc = useQueryClient();

  // Add-new-role state
  const [newName, setNewName] = useState('');
  const [mandatory, setMandatory] = useState<string[]>([]);
  const [beneficial, setBeneficial] = useState<string[]>([]);
  const [other, setOther] = useState<string[]>([]);
  const [mCustom, setMCustom] = useState('');
  const [bCustom, setBCustom] = useState('');
  const [oCustom, setOCustom] = useState('');

  // Inline-edit state
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editM, setEditM] = useState<string[]>([]);
  const [editB, setEditB] = useState<string[]>([]);
  const [editO, setEditO] = useState<string[]>([]);
  const [editMCustom, setEditMCustom] = useState('');
  const [editBCustom, setEditBCustom] = useState('');
  const [editOCustom, setEditOCustom] = useState('');

  // Drag state
  const [dragging, setDragging] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const triggeredInitial = useRef(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: roles = [] } = useQuery({
    queryKey: ['training', 'role-requirements'],
    queryFn: () => api.get<RoleReq[]>('/training/role-requirements').then((r) => r.data),
  });

  // Sync localOrder with server data
  useEffect(() => {
    setLocalOrder(roles.map((r) => r.role));
  }, [roles.length, roles.map((r) => r.role).join('|')]);

  // Auto-open initial edit role (matches prototype's openRoleManagerAndEdit)
  useEffect(() => {
    if (initialEditRole && !triggeredInitial.current && roles.find((r) => r.role === initialEditRole)) {
      triggeredInitial.current = true;
      setTimeout(() => beginEdit(initialEditRole), 60);
    }
  }, [initialEditRole, roles]);

  const orderedRoles = useMemo(() => {
    const map = new Map(roles.map((r) => [r.role, r]));
    return localOrder.map((role) => map.get(role)).filter((r): r is RoleReq => !!r);
  }, [roles, localOrder]);

  const { values: dbCertTypes } = useReferenceList('cert-types', DEFAULT_CERT_TYPES);

  const allCertTypes = useMemo(() => {
    // Settings DB list is the primary source. Fold in any cert types currently
    // referenced by existing role requirements so a freshly created role doesn't
    // lose certs that haven't been added to /settings/cert-types yet.
    const set = new Set<string>(dbCertTypes);
    for (const r of roles) {
      r.mandatory.forEach((c) => set.add(c));
      r.beneficial.forEach((c) => set.add(c));
      r.other.forEach((c) => set.add(c));
    }
    return Array.from(set).sort();
  }, [roles, dbCertTypes]);

  const save = useMutation({
    mutationFn: async () => api.put('/training/role-requirements', {
      role: newName.trim(),
      mandatory: [...mandatory, ...mCustom.split(',').map((s) => s.trim()).filter(Boolean)],
      beneficial: [...beneficial, ...bCustom.split(',').map((s) => s.trim()).filter(Boolean)],
      other: [...other, ...oCustom.split(',').map((s) => s.trim()).filter(Boolean)],
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training', 'role-requirements'] });
      setNewName(''); setMandatory([]); setBeneficial([]); setOther([]); setMCustom(''); setBCustom(''); setOCustom('');
      toast.success('Role added');
    },
    onError: () => toast.error('Save failed'),
  });

  const del = useMutation({
    mutationFn: async (role: string) => api.delete(`/training/role-requirements/${encodeURIComponent(role)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training', 'role-requirements'] });
      toast.info('Role removed');
    },
  });

  const editSave = useMutation({
    mutationFn: async () => {
      if (!editingRole) return;
      return api.put('/training/role-requirements', {
        role: editingRole,
        mandatory: [...new Set([...editM, ...editMCustom.split(',').map((s) => s.trim()).filter(Boolean)])],
        beneficial: [...new Set([...editB, ...editBCustom.split(',').map((s) => s.trim()).filter(Boolean)])],
        other: [...new Set([...editO, ...editOCustom.split(',').map((s) => s.trim()).filter(Boolean)])],
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training', 'role-requirements'] });
      toast.success('Role updated', editingRole ?? '');
      setEditingRole(null);
    },
    onError: () => toast.error('Save failed'),
  });

  const reorder = useMutation({
    mutationFn: async (order: string[]) => api.post('/training/role-requirements/reorder', { order }),
    onError: () => {
      toast.error('Reorder failed');
      qc.invalidateQueries({ queryKey: ['training', 'role-requirements'] });
    },
  });

  function check(list: string[], setList: (v: string[]) => void, v: string) {
    if (list.includes(v)) setList(list.filter((x) => x !== v));
    else setList([...list, v]);
  }

  function beginEdit(role: string) {
    const r = roles.find((x) => x.role === role);
    if (!r) return;
    setEditingRole(role);
    const known = new Set(allCertTypes);
    setEditM(r.mandatory.filter((c) => known.has(c)));
    setEditB(r.beneficial.filter((c) => known.has(c)));
    setEditO(r.other.filter((c) => known.has(c)));
    setEditMCustom(r.mandatory.filter((c) => !known.has(c)).join(', '));
    setEditBCustom(r.beneficial.filter((c) => !known.has(c)).join(', '));
    setEditOCustom(r.other.filter((c) => !known.has(c)).join(', '));
  }

  function cancelEdit() {
    setEditingRole(null);
    setEditM([]); setEditB([]); setEditO([]);
    setEditMCustom(''); setEditBCustom(''); setEditOCustom('');
  }

  function handleDragStart(role: string) { setDragging(role); }
  function handleDrop(targetRole: string) {
    if (!dragging || dragging === targetRole) { setDragging(null); return; }
    const next = [...localOrder];
    const fromIdx = next.indexOf(dragging);
    const toIdx = next.indexOf(targetRole);
    if (fromIdx < 0 || toIdx < 0) { setDragging(null); return; }
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, dragging);
    setLocalOrder(next);
    setDragging(null);
    reorder.mutate(next);
  }

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 720, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">⚙ Role &amp; Training Requirements Manager</div>
        <div className="modal-sub">Add, edit or remove roles. Select certificates for each category — or type new ones. Drag the ⠿ handle to reorder.</div>

        <div id="role-manager-list" style={{ maxHeight: 420, overflowY: 'auto', marginTop: 10 }}>
          {orderedRoles.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, padding: 14, textAlign: 'center' }}>No role requirements defined yet.</div>
          ) : orderedRoles.map((r) => {
            const isEditing = editingRole === r.role;
            const id = safeId(r.role);
            return (
              <div
                key={r.role}
                draggable
                onDragStart={() => handleDragStart(r.role)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(r.role)}
                style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 8, cursor: 'grab', opacity: dragging === r.role ? 0.5 : 1, transition: 'opacity .15s' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: 'var(--faint)', fontSize: 14, cursor: 'grab' }}>⠿</span>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13 }}>{r.role}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <button className="btn btn-ghost btn-xs" onClick={() => isEditing ? cancelEdit() : beginEdit(r.role)}>{isEditing ? 'Cancel' : '✏ Edit'}</button>
                    <button className="btn btn-danger btn-xs" onClick={() => { if (confirm(`Remove ${r.role}?`)) del.mutate(r.role); }} disabled={del.isPending}>✕</button>
                  </div>
                </div>

                {isEditing ? (
                  <div id={`role-edit-${id}`} style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 8 }}>Tick to add to a category. Untick to remove. Add custom certs in the text boxes below each list.</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      {(
                        [
                          { cat: 'mandatory' as Cat, color: 'var(--accent)', list: editM, setList: setEditM, custom: editMCustom, setCustom: setEditMCustom },
                          { cat: 'beneficial' as Cat, color: 'var(--info)', list: editB, setList: setEditB, custom: editBCustom, setCustom: setEditBCustom },
                          { cat: 'other' as Cat, color: 'var(--faint)', list: editO, setList: setEditO, custom: editOCustom, setCustom: setEditOCustom },
                        ]
                      ).map((cfg) => (
                        <div key={cfg.cat}>
                          <div style={{ fontSize: 9, color: cfg.color, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>{cfg.cat} ({cfg.list.length})</div>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 7, minHeight: 80, maxHeight: 220, overflowY: 'auto' }}>
                            {allCertTypes.length === 0 ? (
                              <div style={{ fontSize: 10, color: 'var(--faint)', padding: 4 }}>No certificate types defined yet</div>
                            ) : allCertTypes.map((t) => (
                              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 10, padding: '3px 0', borderBottom: '1px solid rgba(30,42,64,.2)' }}>
                                <input type="checkbox" name={`re-${id}-${cfg.cat}`} value={t} checked={cfg.list.includes(t)} onChange={() => check(cfg.list, cfg.setList, t)} style={{ accentColor: 'var(--accent)', flexShrink: 0 }} />
                                <span>{t}</span>
                              </label>
                            ))}
                          </div>
                          <input type="text" placeholder="Add unlisted cert (comma sep)…" id={`re-${id}-${cfg.cat}-custom`} className="form-input" style={{ marginTop: 5, fontSize: 11 }} value={cfg.custom} onChange={(e) => cfg.setCustom(e.target.value)} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
                      <button className="btn btn-ghost btn-xs" onClick={cancelEdit}>Cancel</button>
                      <button className="btn btn-primary btn-xs" onClick={() => editSave.mutate()} disabled={editSave.isPending}>{editSave.isPending ? 'Saving…' : '✓ Save'}</button>
                    </div>
                  </div>
                ) : (
                  <div id={`role-view-${id}`} style={{ fontSize: 11 }}>
                    {r.mandatory.length ? <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 10 }}>MANDATORY: </span><span style={{ color: 'var(--text)' }}>{r.mandatory.join(', ')}</span></div> : null}
                    {r.beneficial.length ? <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--info)', fontWeight: 600, fontSize: 10 }}>BENEFICIAL: </span><span style={{ color: 'var(--muted)' }}>{r.beneficial.join(', ')}</span></div> : null}
                    {r.other.length ? <div><span style={{ color: 'var(--faint)', fontWeight: 600, fontSize: 10 }}>OTHER: </span><span style={{ color: 'var(--faint)' }}>{r.other.join(', ')}</span></div> : null}
                    {!r.mandatory.length && !r.beneficial.length && !r.other.length ? <span style={{ color: 'var(--faint)' }}>No requirements set</span> : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="divider" style={{ height: 1, background: 'var(--border)', margin: '14px 0' }}></div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 12 }}>+ Add New Role</div>
        <div className="form-row"><label className="form-label">Role Name</label><input type="text" className="form-input" id="nr-name" placeholder="e.g. Traffic Marshal" value={newName} onChange={(e) => setNewName(e.target.value)} /></div>

        {([
          { list: mandatory, setList: setMandatory, custom: mCustom, setCustom: setMCustom, label: 'Mandatory Certificates', id: 'nr-mandatory', color: 'var(--accent)' },
          { list: beneficial, setList: setBeneficial, custom: bCustom, setCustom: setBCustom, label: 'Beneficial Certificates', id: 'nr-beneficial', color: 'var(--info)' },
          { list: other, setList: setOther, custom: oCustom, setCustom: setOCustom, label: 'Other / Reference Only Certificates', id: 'nr-other', color: 'var(--faint)' },
        ] as const).map((cfg) => (
          <div key={cfg.id} className="form-row">
            <label className="form-label">{cfg.label}</label>
            <div id={`${cfg.id}-checks`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, maxHeight: 120, overflowY: 'auto', marginBottom: 6 }}>
              {allCertTypes.map((c) => {
                const checked = cfg.list.includes(c);
                return (
                  <label key={c} style={{
                    display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11,
                    background: checked ? 'rgba(232,164,39,.12)' : 'var(--surface)',
                    border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                    color: checked ? 'var(--accent)' : 'var(--text)',
                    borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap',
                  }}>
                    <input type="checkbox" name={cfg.id} value={c} checked={checked} onChange={() => check(cfg.list, cfg.setList, c)} style={{ accentColor: 'var(--accent)' }} />
                    <span>{c}</span>
                  </label>
                );
              })}
            </div>
            <input type="text" className="form-input" id={`${cfg.id}-custom`} placeholder="Or type additional certs (comma separated)…" value={cfg.custom} onChange={(e) => cfg.setCustom(e.target.value)} style={{ marginTop: 5 }} />
          </div>
        ))}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !newName.trim()}>
            {save.isPending ? 'Saving…' : '+ Add Role'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
